<template>
  <div class="sessions_block">
    <div class="sessions_header">
      <p class="text-xl font-bold">
        /sessions
        <span class="sessions_count">({{ sessionCount }})</span>
      </p>
      <button
        v-if="meetingId && !loading"
        type="button"
        class="sessions_refresh"
        @click="load"
        aria-label="Refresh sessions"
        title="Refresh"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15A9 9 0 1 1 5.64 5.64L23 10" />
        </svg>
      </button>
    </div>

    <div v-if="loading" class="sessions_state">
      &gt;&gt; loading sessions...
    </div>

    <div v-else-if="error" class="sessions_state error">
      &gt;&gt; {{ error }}
    </div>

    <div v-else-if="!sessions.length" class="sessions_state muted">
      -- no sessions yet --
    </div>

    <div v-else class="sessions_list">
      <div
        v-for="s in sessions"
        :key="s.analytics_id"
        class="session_row"
        :class="{ pending: !s.has_data }"
      >
        <span class="session_prompt">&gt;&gt;</span>
        <span class="session_when">{{ formatWhen(s) }}</span>
        <span class="session_sep">|</span>
        <template v-if="s.has_data">
          <span class="session_duration">{{ formatDuration(s) }}</span>
          <span class="session_sep">|</span>
          <span class="session_meta">
            {{ s.user_count }} {{ s.user_count === 1 ? 'user' : 'users' }}
          </span>
          <span class="session_sep">|</span>
          <span class="session_meta">
            {{ s.poll_count }} {{ s.poll_count === 1 ? 'poll' : 'polls' }}
          </span>
        </template>
        <template v-else>
          <span class="session_pending">pending...</span>
        </template>
        <span class="session_sep gt">&gt;</span>
        <button
          type="button"
          class="session_btn"
          :disabled="!s.has_data"
          @click="openSession(s)"
        >
          open
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M7 17L17 7" />
            <polyline points="7 7 17 7 17 17" />
          </svg>
        </button>
        <button
          v-if="s.recording && s.recording.record_id"
          type="button"
          class="session_btn"
          @click="playRecording(s.recording)"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
          recording
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/http/api/api.js';
import constants from '@/../constant.js';

export default {
  name: 'SessionsList',
  props: {
    meetingId: { type: String, default: '' },
  },
  data() {
    return {
      sessions: [],
      sessionCount: 0,
      loading: false,
      error: '',
    };
  },
  watch: {
    meetingId: {
      handler(slug) {
        if (slug) {
          this.load();
        } else {
          this.sessions = [];
          this.sessionCount = 0;
          this.error = '';
        }
      },
      immediate: true,
    },
  },
  methods: {
    async load() {
      if (!this.meetingId) return;
      this.loading = true;
      this.error = '';
      try {
        const url = `${constants.apiCastUrl}/api/event/meeting/sessions/`;
        const res = await api.get(url, {
          params: { meeting_id: this.meetingId },
        });
        const body = (res && res.data) || {};
        this.sessions = Array.isArray(body.sessions) ? body.sessions : [];
        this.sessionCount =
          typeof body.session_count === 'number'
            ? body.session_count
            : this.sessions.length;
      } catch (err) {
        const httpStatus = err && err.response && err.response.status;
        if (httpStatus === 404) {
          this.error = 'not found';
        } else if (httpStatus === 401) {
          this.error = 'sign in to view sessions';
        } else {
          this.error = 'could not load sessions';
        }
        this.sessions = [];
        this.sessionCount = 0;
      } finally {
        this.loading = false;
      }
    },
    formatWhen(session) {
      const iso = session && session.created_at;
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    },
    formatDuration(session) {
      const start = session && session.started_on;
      const end = session && session.ended_on;
      if (!start || !end || end < start) return '-';
      const totalSec = Math.floor((end - start) / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return m > 0 ? `${m}m ${s}s` : `${s}s`;
    },
    openSession(session) {
      if (!session || !session.has_data || !session.analytics_id) return;
      const path = this.$router.resolve({
        path: '/meeting-ended',
        query: { id: session.analytics_id },
      }).href;
      window.open(path, '_blank');
    },
    playRecording(recording) {
      if (!recording || !recording.record_id) return;
      // Prefer BBB's playback URL when present; fall back to letting
      // the host dashboard route into its own playback flow.
      if (recording.playback_url) {
        window.open(recording.playback_url, '_blank', 'noopener');
        return;
      }
      this.$emit('play-recording', recording);
    },
  },
};
</script>

<style scoped>
.sessions_block {
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.sessions_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.sessions_count {
  color: #545454;
  font-weight: 500;
  margin-left: 6px;
}
.sessions_refresh {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: color 0.15s ease, background 0.15s ease;
}
.sessions_refresh:hover {
  color: #d7df23;
  background: rgba(215, 223, 35, 0.06);
}

.sessions_state {
  color: #545454;
  font-size: 14px;
  padding: 6px 0;
}
.sessions_state.muted {
  color: #444;
  font-style: italic;
}
.sessions_state.error {
  color: #f87171;
}

.sessions_list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.session_row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  padding: 4px 0;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}
.session_row.pending {
  opacity: 0.45;
}
.session_prompt {
  color: #545454;
}
.session_when {
  color: #d7df23;
  font-weight: 600;
}
.session_sep {
  color: #404040;
}
.session_sep.gt {
  margin-left: 4px;
}
.session_duration {
  color: rgba(255, 255, 255, 0.7);
}
.session_meta {
  color: rgba(255, 255, 255, 0.65);
}
.session_pending {
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
}

.session_btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(215, 223, 35, 0.05);
  border: 1px solid rgba(215, 223, 35, 0.18);
  color: #d7df23;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.session_btn:hover:not(:disabled) {
  background: rgba(215, 223, 35, 0.12);
  border-color: rgba(215, 223, 35, 0.4);
}
.session_btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
